import { isNil } from 'lodash';
import { toSignificant, toFactor, toPercentage } from '@/utilities/formatting';
import DataTable from '@components/DataTable';

interface DiscountSummaryProps {
  uniqueSymbol: string;
  reportingCurrencyISO: string;
  reportingCurrencySymbol: string;
  primaryTradingCurrecyISO: string;
  primaryTradingCurrencySymbol: string;
  tradingItemCurrencyISO: string;
  tradingItemCurrencySymbol: string;
  exchangeSymbol: string;
  tickerSymbol: string;
  listingPrice: number;
  adrPerShare: number;
  npv: number;
  npvPerShare: number;
  npvPerShareReportedCurrency?: number;
  intrinsicDiscount: number;
  relativeListingSharesOutstanding: number;
  primaryListingExchangeSymbol: string;
  primaryListingTickerSymbol: string;
}

const DiscountSummary = ({
  uniqueSymbol,
  reportingCurrencyISO,
  reportingCurrencySymbol,
  primaryTradingCurrecyISO,
  primaryTradingCurrencySymbol,
  tradingItemCurrencyISO,
  tradingItemCurrencySymbol,
  exchangeSymbol,
  tickerSymbol,
  listingPrice,
  adrPerShare,
  npvPerShare,
  npvPerShareReportedCurrency,
  intrinsicDiscount,
  primaryListingExchangeSymbol,
  primaryListingTickerSymbol,
}: DiscountSummaryProps) => (
  <DataTable light>
    <caption>{`${uniqueSymbol} Discount to Share Price`}</caption>
    <thead>
      <tr>
        <th />
        <th>Calculation</th>
        <th>Result</th>
      </tr>
    </thead>
    <tbody>
      {!isNil(npvPerShareReportedCurrency) &&
        npvPerShareReportedCurrency > 0 &&
        reportingCurrencyISO !== primaryTradingCurrecyISO && (
          <tr>
            <td>Exchange Rate</td>
            <td>
              <span>{`${reportingCurrencyISO}/${primaryTradingCurrecyISO}`}</span>
              <br />
              <span>{`(Reporting currency to currency of ${primaryListingExchangeSymbol}:${primaryListingTickerSymbol})`}</span>
            </td>
            <td>
              {toSignificant(
                (adrPerShare * npvPerShare) / npvPerShareReportedCurrency,
                3
              )}
            </td>
          </tr>
        )}
      {!isNil(npvPerShareReportedCurrency) &&
        npvPerShareReportedCurrency > 0 &&
        reportingCurrencyISO !== primaryTradingCurrecyISO && (
          <tr>
            <td>
              <span>Value per Share </span>
              <br />
              <span>{`(${primaryTradingCurrecyISO})`}</span>
            </td>
            <td>
              <span>{`= Value per Share in ${reportingCurrencyISO} x Exchange Rate (${reportingCurrencyISO} / ${primaryTradingCurrecyISO})`}</span>
              <br />
              <span>{`= ${reportingCurrencySymbol}${toSignificant(
                npvPerShareReportedCurrency,
                2
              )} x ${toSignificant(
                (npvPerShare * adrPerShare) / npvPerShareReportedCurrency,
                2
              )}`}</span>
            </td>
            <td>
              {`${primaryTradingCurrencySymbol}${toSignificant(
                npvPerShare * adrPerShare,
                2
              )}`}
            </td>
          </tr>
        )}
      {adrPerShare > 0 && adrPerShare !== 1 && (
        <tr>
          <td>Non-primary Listing Adjustment Factor</td>
          <td>
            <span>
              {`1 share in ${exchangeSymbol}:${tickerSymbol} represents ${toFactor(
                1 / adrPerShare,
                5
              )} of ${primaryListingExchangeSymbol}:${primaryListingTickerSymbol}`}
            </span>
            <br />
            <span>
              (This could be a different class, a depositary receipt, a
              different currency, or all of these things.)
            </span>
          </td>
          <td>{toFactor(1 / adrPerShare, 5)}</td>
        </tr>
      )}
      {adrPerShare > 0 && adrPerShare !== 1 && (
        <tr>
          <td>
            <span>Value per Share </span>
            <br />
            <span>{`(Listing Adjusted, ${tradingItemCurrencyISO})`}</span>
          </td>
          <td>
            <span>{`= Value per Share (${primaryTradingCurrecyISO}) x Listing Adjustment Factor`}</span>
            <br />
            <span>{`= ${primaryTradingCurrencySymbol}${parseFloat(
              toSignificant(npvPerShare * adrPerShare, 2)
            ).toLocaleString()} x ${toSignificant(1 / adrPerShare, 5)}`}</span>
          </td>
          <td>
            {`${tradingItemCurrencySymbol}${toSignificant(npvPerShare, 2)}`}
          </td>
        </tr>
      )}
      <tr>
        <td>{`Value per share (${tradingItemCurrencyISO})`}</td>
        <td>From above.</td>
        <td>
          {`${tradingItemCurrencySymbol}${toSignificant(npvPerShare, 2)}`}
        </td>
      </tr>
      <tr>
        <td>
          <strong>Current discount</strong>
        </td>
        <td>
          <strong>
            <span>{`Discount to share price of ${tradingItemCurrencySymbol}${toSignificant(
              listingPrice,
              2
            )}`}</span>
            <br />
            <span>{`= (${tradingItemCurrencySymbol}${toSignificant(
              npvPerShare,
              2
            )} - ${tradingItemCurrencySymbol}${toSignificant(
              listingPrice,
              2
            )}) / ${tradingItemCurrencySymbol}${toSignificant(
              npvPerShare,
              2
            )}`}</span>
          </strong>
        </td>
        <td>
          <strong>{toPercentage(intrinsicDiscount, 1)}</strong>
        </td>
      </tr>
    </tbody>
  </DataTable>
);

export default DiscountSummary;
